import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Text from '@components/Text/Text';
import {
  Message,
  MessageTaskAssignment,
  TaskMemberRole,
} from '@graphql/generated';
import useMe from '@hooks/useMe';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { AsyncStorage } from '@utils/storage';

type TaskAssignedProps = {
  message: Message;
};

export const TaskAssigned: React.FC<TaskAssignedProps> = ({ message }) => {
  const { t } = useTranslation('models');
  const { me } = useMe();
  const { attachments, authorFirstName, authorName, authorId, isSender } =
    message;

  const { navigateToTask } = useAppNavigation();
  const assignment = (attachments?.filter(
    (item) => item.__typename === 'MessageTaskAssignment'
  ) ?? [])?.[0] as MessageTaskAssignment;
  const assigneeList = assignment?.assignees;
  const assignmentTask = assignment?.task;

  // The message's assignee snapshot can diverge from the task's real
  // assignees (e.g. onboarding/seed tasks) — prefer the task's live,
  // server-computed role and only fall back to the snapshot if the task
  // itself isn't available (e.g. deleted).
  const isAssignee = assignmentTask
    ? assignmentTask.currentUserRole === TaskMemberRole.Assignee
    : !!assigneeList?.some((u) => u.id === me?.id);

  const inviterFirstName = authorFirstName ?? authorName?.split(' ')[0] ?? '';
  const assigneeName = assigneeList?.[0]?.name ?? '';
  const assigneeFirstName = assigneeList?.[0]?.firstName ?? assigneeName;

  let titleCopy;
  if (isAssignee) {
    titleCopy = t('chat.inviterAddedYouToThisTask', {
      name: inviterFirstName,
    });
  } else if (isSender) {
    titleCopy = t('chat.youAssignedATaskTo', { name: assigneeName });
  } else {
    titleCopy = t('chat.nameHasBeenAssignedATask', { name: assigneeName });
  }

  return (
    <Box
      backgroundColor='white'
      borderBottomRightRadius='s'
      borderBottomLeftRadius='s'
      borderTopRightRadius='s'>
      <Box borderColor='grey02' borderWidth={1} borderRadius='xs' m='s' p='s'>
        <Box
          mb='m'
          width={116}
          flexShrink={0}
          height={28}
          backgroundColor='greenSecondaryMild'
          borderRadius='m'
          alignItems='center'
          justifyContent='center'>
          <Text variant='metadataSemiBold' color='textSecondary'>
            {t('chat.taskAssignedBadge')}
          </Text>
        </Box>

        <Text variant='labelEmphasized'>{titleCopy}</Text>
        <Text variant='webBodySecondary' mb='m'>
          {isAssignee
            ? t('chat.onboarding.memberJoined.youreNowWorkingOn')
            : `${assigneeFirstName} ${t(
                'chat.onboarding.memberJoined.workingOn'
              )}`}{' '}
          <Text
            mt='xxs'
            variant='webBody'
            style={{ fontWeight: '600' }}
            color='greenSecondary'>
            {assignmentTask?.name ?? 'Task'}
          </Text>
        </Text>
        <Box flexDirection='row' alignItems='center' paddingBottom='s' mr='xl'>
          <Box flex={1}>
            <Button
              fullWidth={true}
              borderColor='black'
              backgroundColor='black'
              variant='create'
              isSmall={true}
              height={48}
              onPress={async () => {
                if (isAssignee && inviterFirstName && authorId) {
                  await AsyncStorage.setItem(
                    'pending_task_invite_tooltip',
                    JSON.stringify({ name: inviterFirstName, userId: authorId })
                  );
                }
                assignmentTask && navigateToTask(assignmentTask);
              }}>
              <Text variant='labelSmall' color='white'>
                {t('chat.onboarding.memberJoined.viewTask')}
              </Text>
            </Button>
          </Box>
          <Box flex={1} ml='m'>
            {!isAssignee && (
              <Button
                fullWidth={true}
                borderColor='black'
                borderWidth={1}
                backgroundColor='white'
                variant='create'
                isSmall={true}
                height={48}
                onPress={async () => {
                  if (!assignmentTask) return;
                  await AsyncStorage.setItem('TaskWebPanelOpenAssignee', '1');
                  navigateToTask(assignmentTask);
                }}>
                <Text variant='labelSmall' color='black'>
                  {t('chat.onboarding.memberJoined.assignMore')}
                </Text>
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
